import { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { clearAllCaches, updateStoredVersion, type CacheCleanupProgress } from '../utils/cacheCleaner';

interface UpdateScreenProps {
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export function UpdateScreen({ onComplete, onError }: UpdateScreenProps) {
  const [progress, setProgress] = useState<CacheCleanupProgress>({
    step: 'Preparando atualização...',
    progress: 0,
    total: 5,
  });
  const [status, setStatus] = useState<'cleaning' | 'success' | 'error'>('cleaning');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    const performUpdate = async () => {
      try {
        const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
        
        await clearAllCaches((progressData) => {
          if (isMounted) {
            setProgress(progressData);
          }
        });

        // Atualizar versão armazenada
        updateStoredVersion();
        
        // Marcar esta versão como processada (para não mostrar mensagem novamente)
        if (currentVersion) {
          localStorage.setItem('app-version-processed', currentVersion);
          localStorage.setItem('app-version-updated', Date.now().toString());
          // Limpar flag de notificação para permitir detecção de novas versões no futuro
          localStorage.removeItem('app-version-last-notified');
        }

        // Forçar atualização do Service Worker se existir
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
              // Se há um worker esperando, forçar ativação
              if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                // Aguardar um pouco para o worker processar
                await new Promise(resolve => setTimeout(resolve, 500));
              }
              
              // Limpar flag de notificação do Service Worker
              localStorage.removeItem('sw-update-notified');
              
              // Marcar que o SW foi atualizado
              localStorage.setItem('sw-updated', Date.now().toString());
              
              // Forçar atualização
              await registration.update();
            }
          } catch (error) {
            console.error('Erro ao atualizar Service Worker:', error);
          }
        }

        if (isMounted) {
          setStatus('success');
          
          setTimeout(() => {
            if (isMounted) {
              window.location.replace(window.location.href);
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Erro durante atualização:', error);
        if (isMounted) {
          setStatus('error');
          setErrorMessage(
            error instanceof Error ? error.message : 'Erro desconhecido durante atualização'
          );
          onError?.(error instanceof Error ? error : new Error(String(error)));
        }
      }
    };

    performUpdate();

    return () => {
      isMounted = false;
    };
  }, [onComplete, onError]);

  const progressPercentage = (progress.progress / progress.total) * 100;

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="text-center mb-6">
          {status === 'cleaning' && (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
              <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          )}
          
          {status === 'success' && (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          )}
          
          {status === 'error' && (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
          )}

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {status === 'cleaning' && 'Atualizando Aplicativo'}
            {status === 'success' && 'Atualização Concluída!'}
            {status === 'error' && 'Erro na Atualização'}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {status === 'cleaning' && 'Por favor, aguarde enquanto limpamos os caches...'}
            {status === 'success' && 'A aplicação será recarregada em instantes.'}
            {status === 'error' && 'Ocorreu um erro durante a atualização.'}
          </p>
        </div>

        {/* Progress Bar */}
        {status === 'cleaning' && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {progress.step}
              </span>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {Math.round(progressPercentage)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
              Etapa {progress.progress} de {progress.total}
            </div>
          </div>
        )}

        {/* Success Message */}
        {status === 'success' && (
          <div className="text-center py-4">
            <p className="text-green-600 dark:text-green-400 font-medium">
              Todos os caches foram limpos com sucesso!
            </p>
          </div>
        )}

        {/* Error Message */}
        {status === 'error' && (
          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">
                {errorMessage || 'Não foi possível completar a atualização automaticamente.'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tentar Novamente
              </button>
              <button
                onClick={() => {
                  // Limpar tudo e recarregar
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition-colors"
              >
                Limpar Tudo
              </button>
            </div>
          </div>
        )}

        {/* Steps List (only during cleaning) */}
        {status === 'cleaning' && (
          <div className="mt-6 space-y-2">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Etapas de Limpeza
            </div>
            {[
              'Service Workers',
              'Cache Storage',
              'Dados Locais',
              'Sessão',
              'Finalização',
            ].map((step, index) => (
              <div
                key={step}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  index + 1 <= progress.progress
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    index + 1 < progress.progress
                      ? 'bg-green-500'
                      : index + 1 === progress.progress
                      ? 'bg-blue-500 animate-pulse'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
                <span>{step}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
